import React, { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Col, Row } from 'react-bootstrap'
import { Section } from '../../../layout'
import { Alert, Button, Form, Icon, Input, Modal, Select, Spinner, Toast } from '../../../components'
import { Api } from '../../../api/Assinafy'

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback

const SecurityTab = ({ user = {} }) => {
  const [mfa, setMfa] = useState(null)
  const [enrollment, setEnrollment] = useState(null)
  const [recoveryCodes, setRecoveryCodes] = useState(null)
  const [newCode, setNewCode] = useState('')
  const [reauthMode, setReauthMode] = useState(user.is_password_set ? 'password' : 'code')
  const [reauthValue, setReauthValue] = useState('')
  const [action, setAction] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const methods = mfa?.methods || []
  const hasExistingMethod = methods.length > 0

  const loadMfa = () => {
    setError('')
    return Api('user/mfaIndex')()
      .then(({ data: { data } }) => setMfa(data))
      .catch((requestError) => {
        setError(errorMessage(requestError, 'Não foi possível carregar a configuração de segurança.'))
      })
  }

  useEffect(() => {
    loadMfa()
  }, [])

  useEffect(() => {
    if (!user.is_password_set) {
      setReauthMode('code')
    }
  }, [user.is_password_set])

  const startEnrollment = (event) => {
    event?.preventDefault()
    setLoading(true)
    setError('')
    Api('user/mfaTotpCreate')({ label: 'Aplicativo autenticador' })
      .then(({ data: { data } }) => {
        setEnrollment(data)
        setNewCode('')
        setReauthValue('')
      })
      .catch((requestError) => {
        setError(errorMessage(requestError, 'Não foi possível iniciar a configuração.'))
      })
      .finally(() => setLoading(false))
  }

  const confirmEnrollment = (event) => {
    event.preventDefault()
    const payload = {
      id: enrollment.id,
      code: newCode.trim(),
    }

    if (hasExistingMethod) {
      payload[reauthMode === 'password' ? 'password' : 'reauth_code'] = reauthValue.trim()
    }

    setLoading(true)
    setError('')
    Api('user/mfaTotpConfirm')(payload)
      .then(({ data: { data } }) => {
        setRecoveryCodes(data.recovery_codes)
        setEnrollment(null)
        setNotice('A verificação em duas etapas está ativa.')
        return loadMfa()
      })
      .catch((requestError) => {
        setError(errorMessage(requestError, 'Não foi possível confirmar o autenticador. Confira os códigos informados.'))
      })
      .finally(() => setLoading(false))
  }

  const closeAction = () => {
    setAction(null)
    setReauthValue('')
    setError('')
  }

  const submitProtectedAction = (event) => {
    event.preventDefault()
    const proof = {
      [reauthMode === 'password' ? 'password' : 'code']: reauthValue.trim(),
    }
    const request = action?.type === 'disable'
      ? Api('user/mfaDelete')(action.methodId, proof)
      : Api('user/mfaRecoveryCodes')(proof)

    setLoading(true)
    setError('')
    request
      .then(({ data: { data } }) => {
        if (action.type === 'regenerate') {
          setRecoveryCodes(data.recovery_codes)
          setNotice('Novos códigos criados. Os códigos anteriores não funcionam mais.')
        } else {
          setNotice('A verificação em duas etapas foi desativada.')
        }
        closeAction()
        return loadMfa()
      })
      .catch((requestError) => {
        setError(errorMessage(requestError, 'A comprovação não foi aceita. Tente novamente.'))
      })
      .finally(() => setLoading(false))
  }

  if (mfa === null) {
    if (!error) {
      return <Section><div className="security-loading"><Spinner className="me-2" /> Carregando segurança...</div></Section>
    }

    return (
      <Section className="section-security">
        <Alert variant="warning">{error}</Alert>
        <Button variant="info" onClick={loadMfa}>TENTAR NOVAMENTE</Button>
      </Section>
    )
  }

  const handleMfaToggle = () => {
    if (loading) {
      return
    }

    if (enrollment && !hasExistingMethod) {
      setEnrollment(null)
      setError('')
      return
    }

    if (hasExistingMethod) {
      setAction({ type: 'disable', methodId: methods[0].id })
      setError('')
      return
    }

    startEnrollment()
  }

  return (
    <Section>
      <Alert show={!!error && !action && !enrollment} variant="warning" onClose={() => setError('')}>
        {error}
      </Alert>

      <Row>
        <Col lg="6">
          <Section.Header
            title="Verificação em duas etapas"
            titleAs="h3"
            opening="Exija um código do aplicativo autenticador sempre que acessar sua conta."
          >
            <Input.Switch
              labelOn="Ativada"
              labelOff="Desativada"
              checked={hasExistingMethod || !!enrollment || (!hasExistingMethod && loading)}
              onChange={handleMfaToggle}
            />
          </Section.Header>

          {enrollment ? (
            <Enrollment
              enrollment={enrollment}
              error={error}
              hasExistingMethod={hasExistingMethod}
              isPasswordSet={user.is_password_set}
              loading={loading}
              newCode={newCode}
              reauthMode={reauthMode}
              reauthValue={reauthValue}
              setNewCode={setNewCode}
              setReauthMode={setReauthMode}
              setReauthValue={setReauthValue}
              onCancel={() => {
                setEnrollment(null)
                setError('')
              }}
              onSubmit={confirmEnrollment}
            />
          ) : hasExistingMethod && <>
            {mfa.recovery_codes_remaining <= 3 && (
              <Alert variant="warning">
                Restam apenas {mfa.recovery_codes_remaining} códigos de recuperação. Gere um novo conjunto para não perder o acesso.
              </Alert>
            )}

            <div className="security-settings-list">
              {methods.map((method) => (
                <div className="security-settings-item" key={method.id}>
                  <div>
                    <div className="security-settings-title">Aplicativo autenticador</div>
                    Ativado em {new Date(method.confirmed_at).toLocaleDateString('pt-BR')}
                  </div>
                  <Button variant="link" onClick={startEnrollment} loading={loading}>
                    SUBSTITUIR
                  </Button>
                </div>
              ))}

              <div className="security-settings-item">
                <div>
                  <div className="security-settings-title">Códigos de recuperação</div>
                  {mfa.recovery_codes_remaining} códigos disponíveis para acesso de emergência.
                </div>
                <Button
                  variant="link"
                  onClick={() => {
                    setAction({ type: 'regenerate' })
                    setError('')
                  }}
                >
                  GERAR NOVOS
                </Button>
              </div>
            </div>
          </>}
        </Col>
      </Row>

      <ReauthModal
        action={action}
        error={error}
        isPasswordSet={user.is_password_set}
        loading={loading}
        mode={reauthMode}
        value={reauthValue}
        onClose={closeAction}
        onModeChange={(mode) => {
          setReauthMode(mode)
          setReauthValue('')
          setError('')
        }}
        onSubmit={submitProtectedAction}
        onValueChange={setReauthValue}
      />

      <RecoveryCodes codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />

      <Toast show={!!notice} variant="success" onClose={() => setNotice('')}>
        {notice}
      </Toast>
    </Section>
  )
}

const Enrollment = ({
  enrollment,
  error,
  hasExistingMethod,
  isPasswordSet,
  loading,
  newCode,
  reauthMode,
  reauthValue,
  setNewCode,
  setReauthMode,
  setReauthValue,
  onCancel,
  onSubmit,
}) => {
  if (!enrollment) return null

  return (
    <div className="security-enrollment">
      <h4>{hasExistingMethod ? 'Substituir aplicativo autenticador' : 'Configurar aplicativo autenticador'}</h4>
      <p>Escaneie o QR code abaixo e confirme com o código exibido no aplicativo.</p>
      <form className="form" onSubmit={onSubmit}>
        <Alert show={!!error} variant="warning">{error}</Alert>
        <div className="security-enrollment-content">
          <div className="security-qr">
            <QRCodeSVG
              value={enrollment.provisioning_uri}
              size={192}
              level="M"
              marginSize={2}
              title="QR code para configurar o aplicativo autenticador"
            />
          </div>
          <div className="security-enrollment-fields">
            <Form.Group>
              <Input
                name="secret"
                label="Chave para configuração manual"
                value={enrollment.secret}
                readOnly
              />
            </Form.Group>
            <Form.Group>
              <Input
                name="code"
                label="Código do novo autenticador"
                value={newCode}
                onChange={(event) => setNewCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                required
                autoFocus
              />
            </Form.Group>

            {hasExistingMethod && (
              <ProofFields
                isPasswordSet={isPasswordSet}
                mode={reauthMode}
                value={reauthValue}
                onModeChange={(mode) => {
                  setReauthMode(mode)
                  setReauthValue('')
                }}
                onValueChange={setReauthValue}
                codeLabel="Código do autenticador atual ou de recuperação"
              />
            )}
          </div>
        </div>
        <Form.Footer horizontal>
          <Button variant="info" onClick={onCancel}>CANCELAR</Button>
          <Button variant="primary" submit loading={loading}>
            <Icon id="icon-check" className="me-1" size="16" />
            CONFIRMAR E ATIVAR
          </Button>
        </Form.Footer>
      </form>
    </div>
  )
}

const ProofFields = ({ isPasswordSet, mode, value, onModeChange, onValueChange, codeLabel }) => (
  <div className="security-proof">
    {isPasswordSet && (
      <Form.Group>
        <Select
          name="reauth_method"
          label="Confirmar identidade com"
          value={mode}
          onChange={(event) => onModeChange(event.target.value)}
          options={[
            { value: 'password', label: 'Senha atual' },
            { value: 'code', label: 'Código do autenticador ou de recuperação' },
          ]}
        />
      </Form.Group>
    )}
    <Form.Group>
      {mode === 'password' ? (
        <Input.Password
          name="password"
          label="Senha atual"
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          autoComplete="current-password"
          required
        />
      ) : (
        <Input
          name="reauth_code"
          label={codeLabel}
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          autoComplete="one-time-code"
          autoCapitalize="characters"
          required
        />
      )}
    </Form.Group>
  </div>
)

const ReauthModal = ({ action, error, isPasswordSet, loading, mode, value, onClose, onModeChange, onSubmit, onValueChange }) => (
  <Modal.Alert
    show={!!action}
    variant={action?.type === 'disable' ? 'danger' : undefined}
    title={action?.type === 'disable' ? 'Desativar verificação em duas etapas' : 'Gerar novos códigos de recuperação'}
    description="Confirme sua identidade antes de continuar."
    onClose={onClose}
  >
    <form className="form" onSubmit={onSubmit}>
      <Modal.Body>
        <Alert show={!!error} variant="warning">{error}</Alert>
        <ProofFields
          isPasswordSet={isPasswordSet}
          mode={mode}
          value={value}
          onModeChange={onModeChange}
          onValueChange={onValueChange}
          codeLabel="Código do autenticador ou de recuperação"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="info" onClick={onClose}>CANCELAR</Button>
        <Button variant={action?.type === 'disable' ? 'danger' : 'primary'} submit loading={loading}>
          {action?.type === 'disable' ? 'DESATIVAR' : 'GERAR CÓDIGOS'}
        </Button>
      </Modal.Footer>
    </form>
  </Modal.Alert>
)

export const RecoveryCodes = ({ codes, onDone }) => {
  const [copyStatus, setCopyStatus] = useState({ type: '', message: '' })

  if (!codes) return null

  const copyCodes = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable')
      await navigator.clipboard.writeText(codes.join('\n'))
      setCopyStatus({ type: 'success', message: 'Códigos copiados para a área de transferência.' })
    } catch (error) {
      setCopyStatus({ type: 'error', message: 'Não foi possível copiar. Selecione os códigos ou baixe o arquivo.' })
    }
  }
  const downloadCodes = () => {
    const url = URL.createObjectURL(new Blob([codes.join('\n')], { type: 'text/plain' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'assinafy-codigos-recuperacao.txt'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Modal.Alert
      show
      className="modal-recovery-codes"
      title="Códigos de recuperação"
      description={<><p>Guarde estes códigos em um local seguro.</p><b>Eles são exibidos apenas uma vez e cada código pode ser usado uma única vez.</b></>}
    >
      <Modal.Body>
        <div className="security-recovery-codes">
          {codes.map((code) => <code key={code}>{code}</code>)}
        </div>
        <div className="security-recovery-actions">
          <Button variant="link" onClick={copyCodes}>
            <Icon id={copyStatus.type === 'success' ? 'icon-check' : 'icon-copy'} className="me-1" size="16" />
            COPIAR
          </Button>
          <Button variant="link" onClick={downloadCodes}>
            <Icon id="icon-file" className="me-1" size="16" />
            BAIXAR ARQUIVO
          </Button>
        </div>
        <span
          className={`security-copy-status ${copyStatus.type ? `is-${copyStatus.type}` : ''}`}
          role="status"
        >
          {copyStatus.message}
        </span>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onDone}>
          <Icon id="icon-check" className="me-1" size="16" />
          JÁ SALVEI OS CÓDIGOS
        </Button>
      </Modal.Footer>
    </Modal.Alert>
  )
}

export default SecurityTab
